import { useState, useEffect, useMemo } from 'react';
import {
  DoorOpen,
  Shield,
  Clock,
  Users,
  LogIn,
  LogOut,
  AlertTriangle,
  Radio,
  Building2,
  Activity,
  Map as MapIcon,
  Zap,
  TrendingUp,
  Cpu,
  FileText,
  X,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

import { useAuthStore } from '../../store/authStore';
import { useHojeReferencia } from '../../api/hooks/useSimClock';
import { useDashboardApi, LoadingSpinner, ErrorPanel } from '../../components/dashboard/DashboardShared';

import './acesso-war-room.css';
import '../professor/professor-dashboard-desktop.css';

const PIE_COLORS = ['#22c55e', '#f59e0b', '#ef4444', '#94a3b8', '#cbd5e1'];

/** Cores fixas por categoria (não por índice — evita Ausentes virar laranja). */
const DONUT_COLOR_BY_NAME: Record<string, string> = {
  Presentes: '#22c55e',
  Atrasados: '#f59e0b',
  Ausentes: '#ef4444',
  Aguardando: '#94a3b8',
  'Sem dados': '#cbd5e1',
};

function donutColor(name: string, index = 0): string {
  return DONUT_COLOR_BY_NAME[name] || PIE_COLORS[index % PIE_COLORS.length];
}

const tooltipStyle = {
  fontSize: 10,
  fontWeight: 500,
  borderRadius: 6,
  border: '1px solid #e8ecf1',
  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)',
};

const PONTO_LEGENDA: { label: string; cor: string; desc: string }[] = [
  { label: 'Presente', cor: '#22c55e', desc: 'Aluno presente na escola' },
  { label: 'Atrasado', cor: '#f59e0b', desc: 'Chegou com atraso' },
  { label: 'Ausente', cor: '#ef4444', desc: 'Aluno ausente' },
  { label: 'Sem registro', cor: '#cbd5e1', desc: 'Sem dados de acesso hoje' },
];

function pontoClass(p: string) {
  return `ac-war-ponto ac-war-ponto--${p}`;
}

/** Agrupa alunos em fileiras (visão sala de aula). */
function pontosEmFileiras(pontos: string[], colunas = 7): string[][] {
  const fileiras: string[][] = [];
  for (let i = 0; i < pontos.length; i += colunas) {
    fileiras.push(pontos.slice(i, i + colunas));
  }
  return fileiras;
}

function colunasSala(total: number): number {
  if (total <= 20) return 5;
  if (total <= 28) return 7;
  return 8;
}

const TURNOS_ORDEM = ['Manhã', 'Tarde', 'Noite'] as const;

/** Normaliza nome de turno para agrupamento (Manhã / Tarde / Noite). */
function canonicalTurnoLabel(nome: string): string {
  const n = String(nome || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
  if (n.includes('MANHA') || n.includes('MATUT')) return 'Manhã';
  if (n.includes('TARDE') || n.includes('VESP')) return 'Tarde';
  if (n.includes('NOITE') || n.includes('NOTUR')) return 'Noite';
  const trimmed = String(nome || '').trim();
  return trimmed || 'Sem turno';
}

function truncateLabel(s: string, max: number): string {
  const t = String(s || '').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function hmShortLabel(hora: string): string {
  const h = String(hora || '').trim();
  return h.length >= 5 ? h.slice(0, 5) : h || '—';
}

function agruparTurmasPorTurno(turmas: any[]): Array<{ turno: string; turmas: any[] }> {
  const map = new Map<string, any[]>();
  for (const t of turmas) {
    const key = canonicalTurnoLabel(t?.turma?.turno || t?.turno || '');
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(t);
  }
  const keys = [...map.keys()].sort((a, b) => {
    const ia = TURNOS_ORDEM.indexOf(a as (typeof TURNOS_ORDEM)[number]);
    const ib = TURNOS_ORDEM.indexOf(b as (typeof TURNOS_ORDEM)[number]);
    if (ia === -1 && ib === -1) return a.localeCompare(b, 'pt-BR');
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  return keys.map((turno) => ({
    turno,
    turmas: (map.get(turno) || []).slice().sort((a, b) =>
      String(a?.turma?.nome || '').localeCompare(String(b?.turma?.nome || ''), 'pt-BR'),
    ),
  }));
}

export function AcessoWarRoomDesktop() {
  const { user, escolaId: escolaIdStore } = useAuthStore();
  const escolaId = user?.escolaId || escolaIdStore || null;
  const { hojeStr, isSimulation } = useHojeReferencia();
  const [turnoFiltro, setTurnoFiltro] = useState<string | null>(null);
  const [relatorioAtrasos, setRelatorioAtrasos] = useState<'turmas' | 'alunos' | null>(null);
  const [turmaDetalhe, setTurmaDetalhe] = useState<{
    turmaId: string;
    nome: string;
    turno: string;
    futuro: boolean;
    matriculados: number;
    presentes: number;
    atrasados: number;
    ausentes: number;
    aguardando: number;
    entraramRfid: number;
  } | null>(null);

  const [horaAtual, setHoraAtual] = useState(
    new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
  );

  useEffect(() => {
    const t = setInterval(() => {
      setHoraAtual(
        new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      );
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const endpoint = useMemo(() => {
    if (
      escolaId &&
      (user?.perfil === 'ESCOLA' || user?.perfil === 'PROFESSOR' || !user?.perfil)
    ) {
      return `/escolas/${escolaId}/dashboard/war-room`;
    }
    if (user?.perfil === 'REDE' && user?.redeId) return `/redes/${user.redeId}/dashboard`;
    if (user?.perfil === 'MUNICIPAL' && (user as any)?.municipioId) {
      return `/municipios/${(user as any).municipioId}/dashboard`;
    }
    if (user?.perfil === 'ESTADUAL' && (user as any)?.estadoId) {
      return `/estados/${(user as any).estadoId}/dashboard`;
    }
    if (user?.perfil === 'ADMIN') {
      return escolaId ? `/escolas/${escolaId}/dashboard/war-room` : `/master/dashboard`;
    }
    return '';
  }, [user, escolaId]);

  const warRoomParams = useMemo(
    () => ({
      data: hojeStr,
      turno: turnoFiltro || 'ESCOLA',
    }),
    [hojeStr, turnoFiltro],
  );

  const { data, isLoading, error, isFetching, dataUpdatedAt } = useDashboardApi(
    `war-room-${user?.id}-${escolaId}`,
    endpoint,
    {
      params: endpoint.includes('/war-room') ? warRoomParams : undefined,
      refetchInterval: endpoint.includes('/war-room') ? 5000 : 30000,
    },
  );

  // Default: consolidado da escola (não um turno isolado).
  useEffect(() => {
    if (!turnoFiltro) setTurnoFiltro('ESCOLA');
  }, [turnoFiltro]);

  if (!endpoint) {
    return (
      <ErrorPanel message="Sessão sem escola vinculada. Reabra a janela da escola no simulador." />
    );
  }
  if (isLoading) return <LoadingSpinner label="Sincronizando War Room..." />;
  if (error || !data) {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    return (
      <ErrorPanel
        message={
          status === 401 || status === 403
            ? 'Não foi possível autenticar o War Room da portaria. Recarregue a janela da escola.'
            : status
              ? `Erro ao carregar portaria (HTTP ${status}).`
              : 'Erro ao carregar dashboard da portaria.'
        }
      />
    );
  }

  // --------------------------------------------------------
  // MAPEAMENTO DA API
  // --------------------------------------------------------
  const isEscola = user?.perfil === 'ESCOLA' || !!escolaId;
  const c = data.cards || {};
  
  const alunosRede = c.totalPrevistos || c.totalAlunosPrevistos || 0;
  const presentes = c.totalPresentes || 0;
  const atrasados = c.totalAtrasados || 0;
  const ausentes = c.totalAusentes || 0;
  const aguardando = c.totalAguardando || 0;
  const esperado = c.totalEsperado ?? alunosRede;
  const real = c.totalReal ?? c.totalEntraram ?? 0;
  const divergencia = c.divergencia ?? Math.max(0, esperado - real);
  const escolasMonitoradas = c.totalEscolas || 1;
  const naEscola = presentes + atrasados;
  const pctPresenca = esperado > 0 ? Math.round((real / esperado) * 100) : 0;
  const pctAusencia = esperado > 0 ? Math.round((ausentes / esperado) * 100) : 0;
  const consolidado = data.consolidadoEscola || null;
  const vistaEscola = data.vista === 'escola' || turnoFiltro === 'ESCOLA';
  
  // Leitores RFID (Catracas e Coletores reais da API)
  const leitoresTotal = data.operacao?.totalLeitores || escolasMonitoradas * 2; 
  const dispositivosComProblema = data.operacao?.dispositivosProblema || data.operacao?.dispositivosProblematicos || [];
  const leitoresOffline = dispositivosComProblema.length;
  const leitoresOnline = Math.max(0, leitoresTotal - leitoresOffline);
  const alertasAtivos = c.alertasAtivos ?? c.totalAlertasAtivos ?? 0;

  const dataRefLabel = (() => {
    const ymd = data.dataReferencia || hojeStr;
    const [y, m, d] = String(ymd).split('-').map(Number);
    if (!y || !m || !d) return hojeStr;
    return new Date(y, m - 1, d).toLocaleDateString('pt-BR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  })();

  const feed = data.operacao?.feed || [];
  const alertas = data.alertas?.incidentesAtivos || data.alertas?.criticos || [];
  const fluxoHora = data.fluxoHora || [];
  const censoPorTurno = data.censoPorTurno || [];
  const turnosApi = data.turnos || [];
  
  // Turmas (apenas para ESCOLA, ou turmas consolidadas se vier da API)
  const turmasRaw = data.analitico?.resumoTurmas || [];
  const turmasPorTurno = agruparTurmasPorTurno(turmasRaw);
  // Relatório completo turmas (atraso > 0)
  const atrasosTurmasAll = [...turmasRaw]
    .filter((t) => (t.atrasados || 0) > 0 && t.fase !== 'futuro' && t.contabiliza !== false)
    .sort((a, b) => (b.atrasados || 0) - (a.atrasados || 0))
    .map((t) => {
      const turno = canonicalTurnoLabel(t.turma?.turno || t.turno || '');
      const nome = String(t.turma?.nome || 'Turma').replace(/\s+/g, ' ').trim();
      return {
        id: t.turma?.id || nome,
        label: `${nome} · ${turno}`,
        turma: `${nome} · ${turno}`,
        turno,
        valor: t.atrasados || 0,
        matriculados: t.matriculados || 0,
      };
    });
  const topAtrasos = atrasosTurmasAll.slice(0, 8);

  const atrasosAlunosAll = (data.analitico?.atrasosAlunos || []) as Array<{
    alunoId: string;
    nome: string;
    turmaId?: string | null;
    turma: string;
    turno: string;
    hora: string;
    minutosAtraso: number;
  }>;
  const ausentesAlunosAll = (data.analitico?.ausentesAlunos || []) as Array<{
    alunoId: string;
    nome: string;
    turmaId?: string | null;
    turma: string;
    turno: string;
  }>;
  const atrasosAlunosTurmaDetalhe = !turmaDetalhe
    ? []
    : atrasosAlunosAll.filter((a) => {
        if (turmaDetalhe.turmaId && a.turmaId && a.turmaId === turmaDetalhe.turmaId) {
          return true;
        }
        return (
          a.turma === turmaDetalhe.nome &&
          canonicalTurnoLabel(a.turno || '') === turmaDetalhe.turno
        );
      });
  const ausentesAlunosTurmaDetalhe = !turmaDetalhe
    ? []
    : ausentesAlunosAll.filter((a) => {
        if (turmaDetalhe.turmaId && a.turmaId && a.turmaId === turmaDetalhe.turmaId) {
          return true;
        }
        return (
          a.turma === turmaDetalhe.nome &&
          canonicalTurnoLabel(a.turno || '') === turmaDetalhe.turno
        );
      });
  const topAtrasosAlunos = atrasosAlunosAll.slice(0, 8).map((a) => {
    const turno = canonicalTurnoLabel(a.turno || '');
    const primeiro = String(a.nome || 'Aluno').split(/\s+/)[0];
    return {
      ...a,
      label: `${primeiro} · ${hmShortLabel(a.hora)}`,
      chartLabel: truncateLabel(`${primeiro} (${a.turma})`, 22),
      turno,
      valor: a.minutosAtraso,
    };
  });
  const alertasCount = data.cards?.alertasAtivos ?? alertas.length;

  // Panorama = quem entrou (RFID real) vs ausentes; atrasados entram em Presentes se a API não separar.
  const presentesDonut = Math.max(0, (real || naEscola) - (atrasados > 0 ? atrasados : 0));
  const donutPresenca = [
    { name: 'Presentes', value: atrasados > 0 ? presentesDonut : real || naEscola || presentes },
    ...(atrasados > 0 ? [{ name: 'Atrasados', value: atrasados }] : []),
    { name: 'Ausentes', value: ausentes },
    ...(aguardando > 0 ? [{ name: 'Aguardando', value: aguardando }] : []),
  ].filter((d) => d.value > 0);
  if (donutPresenca.length === 0) {
    donutPresenca.push({ name: 'Sem dados', value: 1 });
  }

  const statusEstados = data.operacao?.statusEstados || [];
  const turnoAtual =
    turnoFiltro === 'ESCOLA' || !turnoFiltro
      ? 'Escola'
      : turnoFiltro || data.turnoOperacional || 'Turno';
  const atualizadoEm = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '—';
  const horaRef = data.horaReferencia || '—';
  const faseLabel = (fase?: string) => {
    if (fase === 'passado') return 'feito';
    if (fase === 'presente') return 'agora';
    if (fase === 'futuro') return 'futuro';
    return '';
  };

  return (
    <div className="admin-page ac-war-page text-[13px] leading-snug animate-fadeIn">
      <div className="ac-war-scroll">
        <header className="ac-war-hero">
          <div className="ac-war-hero-grid" aria-hidden />
          <div className="ac-war-hero-glow" aria-hidden />
          <div className="ac-war-hero-inner">
            <div className="ac-war-hero-main">
              <div className="ac-war-hero-topline">
                <span className="ac-war-hero-badge" style={{ background: '#22c55e', color: '#fff', border: 'none' }}>
                  <i style={{ background: '#fff' }} /> {isFetching ? 'Atualizando…' : 'Ao vivo'}
                </span>
                <h1>Portaria · {isEscola ? 'Unidade Escolar' : 'Rede'}</h1>
                <span className="ac-war-hero-dot" aria-hidden>
                  ·
                </span>
                <span className="ac-war-hero-meta">
                  {isSimulation ? 'Dia simulado' : 'Dia'} {dataRefLabel}
                  {' · '}
                  relógio {horaRef}
                  {' · '}
                  {vistaEscola ? 'consolidado escola' : `turno ${turnoAtual}`}
                  {' · '}
                  sync {atualizadoEm}
                </span>
              </div>
              <div className="ac-war-hero-chips" aria-label="Resumo da portaria">
                <span className="ac-war-hero-chip ac-war-hero-chip--ok">
                  <strong>{pctPresenca}%</strong> real/esperado
                </span>
                <span className="ac-war-hero-chip">
                  <strong>{real.toLocaleString('pt-BR')}</strong>/
                  {esperado.toLocaleString('pt-BR')} RFID
                </span>
                <span className="ac-war-hero-chip ac-war-hero-chip--alert">
                  <strong>{divergencia.toLocaleString('pt-BR')}</strong> divergência
                </span>
                {aguardando > 0 ? (
                  <span className="ac-war-hero-chip ac-war-hero-chip--future">
                    <strong>{aguardando.toLocaleString('pt-BR')}</strong> aguardando
                  </span>
                ) : null}
                <span className="ac-war-hero-chip ac-war-hero-chip--alert">
                  <strong>{alertasAtivos}</strong> alertas
                </span>
              </div>
            </div>
            <div className="ac-war-clock" aria-live="polite">
              <Clock size={15} strokeWidth={2} />
              {horaAtual}
            </div>
          </div>
        </header>

        {(turnosApi.length > 0 || censoPorTurno.length > 0) && (
          <div className="ac-war-turnos" role="tablist" aria-label="Filtro por turno">
            {(turnosApi.length > 0
              ? turnosApi
              : [
                  {
                    id: 'ESCOLA',
                    nome: 'Escola',
                    isAtivo: false,
                    fase: 'presente',
                    isConsolidado: true,
                  },
                  ...censoPorTurno.map((t: any) => ({
                    id: t.turnoId,
                    nome: t.turnoNome,
                    isAtivo: t.isTurnoAtivo,
                    fase: t.fase,
                  })),
                ]
            ).map((t: any) => {
              const censo = censoPorTurno.find(
                (row: any) => row.turnoNome === t.nome || row.turnoId === t.id,
              );
              const on =
                turnoFiltro === t.nome ||
                (t.isConsolidado && turnoFiltro === 'ESCOLA');
              const fase = t.fase || censo?.fase || 'presente';
              return (
                <button
                  key={t.id || t.nome}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  className={`ac-war-turno-btn fase-${fase}${on ? ' is-on' : ''}${t.isAtivo ? ' is-ativo' : ''}${t.isConsolidado ? ' is-escola' : ''}`}
                  onClick={() =>
                    setTurnoFiltro(t.isConsolidado ? 'ESCOLA' : t.nome)
                  }
                >
                  <span className="ac-war-turno-nome">
                    {t.nome}
                    {t.isAtivo ? <em>agora</em> : null}
                    {!t.isConsolidado && fase ? (
                      <em className={`fase-${fase}`}>{faseLabel(fase)}</em>
                    ) : null}
                    {t.isConsolidado ? <em>consolidado</em> : null}
                  </span>
                  {t.isConsolidado && consolidado ? (
                    <span className="ac-war-turno-meta">
                      <strong className="ok">{consolidado.realPassadoPresente}</strong>
                      {' / '}
                      {consolidado.esperadoPassadoPresente}
                      {consolidado.aguardandoFuturo > 0
                        ? ` · ${consolidado.aguardandoFuturo} futuro`
                        : ''}
                    </span>
                  ) : censo ? (
                    <span className="ac-war-turno-meta">
                      {(censo.alunos ?? 0) === 0 ? (
                        <>sem turmas neste turno</>
                      ) : fase === 'futuro' ? (
                        <>
                          <strong className="wait">{censo.aguardando ?? censo.alunos}</strong>{' '}
                          aguardando
                        </>
                      ) : (
                        <>
                          <strong className="ok">{censo.real ?? censo.entraram}</strong>
                          {' / '}
                          {censo.esperado ?? censo.alunos}
                          {' · '}
                          <strong className="aus">{censo.ausentes}</strong> aus
                        </>
                      )}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}

        <p className="ac-war-fase-note">
          <strong>Real</strong> = RFID já ocorrido (passado + turno atual) ·{' '}
          <strong>Esperado</strong> = alunos desses turnos ·{' '}
          <strong>Futuro</strong> = exibido, sem contar como ausência.
        </p>

        <div className="ac-war-kpis">
          {[
            {
              label: vistaEscola ? 'Esperado (feito+agora)' : `Esperado · ${turnoAtual}`,
              val: esperado.toLocaleString('pt-BR'),
              color: '#2563eb',
              bg: '#eff6ff',
              Icon: Users,
            },
            {
              label: 'Real (RFID)',
              val: real.toLocaleString('pt-BR'),
              color: '#16a34a',
              bg: '#f0fdf4',
              Icon: Shield,
            },
            {
              label: 'Atrasados',
              val: atrasados.toLocaleString('pt-BR'),
              color: '#d97706',
              bg: '#fffbeb',
              Icon: Clock,
            },
            {
              label: 'Ausentes (real)',
              val: ausentes.toLocaleString('pt-BR'),
              color: '#dc2626',
              bg: '#fef2f2',
              Icon: AlertTriangle,
            },
            {
              label: 'Aguardando (futuro)',
              val: aguardando.toLocaleString('pt-BR'),
              color: '#64748b',
              bg: '#f8fafc',
              Icon: Activity,
            },
            {
              label: 'Leitores online',
              val: `${leitoresOnline}/${leitoresTotal}`,
              color: '#0891b2',
              bg: '#ecfeff',
              Icon: Radio,
            },
          ].map((k) => (
            <div key={k.label} className="ac-war-kpi">
              <div>
                <div className="ac-war-kpi-label">{k.label}</div>
                <div className="ac-war-kpi-val" style={{ color: k.color }}>
                  {k.val}
                </div>
              </div>
              <div className="ac-war-kpi-icon" style={{ background: k.bg, color: k.color }}>
                <k.Icon size={14} />
              </div>
            </div>
          ))}
        </div>

        <div className="ac-war-grid">
          {/* Fluxo entrada/saída */}
          <div className="ac-war-card ac-war-span-2">
            <div className="ac-war-card-head">
              <h3>
                <Activity size={12} /> Fluxo portaria — entradas e saídas por hora
              </h3>
            </div>
            <div className="ac-war-card-body">
              <div className="ac-war-chart-h">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={fluxoHora.length > 0 ? fluxoHora : []} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gEnt" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gSai" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ef4444" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="hora" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={28} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Area
                      type="monotone"
                      dataKey="entradas"
                      stroke="#2563eb"
                      fill="url(#gEnt)"
                      strokeWidth={2}
                      name="Entradas (real)"
                      strokeDasharray={undefined}
                    />
                    <Area
                      type="monotone"
                      dataKey="saidas"
                      stroke="#ef4444"
                      fill="url(#gSai)"
                      strokeWidth={1.5}
                      name="Saídas"
                    />
                  </AreaChart>
                </ResponsiveContainer>
                <p className="ac-war-fase-note" style={{ marginTop: 6, marginBottom: 0 }}>
                  Horas futuras no relógio ({horaRef}) ficam sem volume real — não entram no consolidado.
                </p>
              </div>
            </div>
          </div>

          {/* Donut presença rede */}
          <div className="ac-war-card">
            <div className="ac-war-card-head">
              <h3>
                <TrendingUp size={12} /> Panorama
              </h3>
            </div>
            <div className="ac-war-card-body">
              <div className="ac-war-chart-h-sm">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={donutPresenca}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      innerRadius="55%"
                      outerRadius="82%"
                      paddingAngle={2}
                      stroke="none"
                      startAngle={90}
                      endAngle={-270}
                    >
                      {donutPresenca.map((d, i) => (
                        <Cell key={d.name} fill={donutColor(d.name, i)} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginTop: 4 }}>
                {donutPresenca.map((d, i) => (
                  <span key={d.name} style={{ fontSize: '0.58rem', fontWeight: 600, color: '#64748b' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: donutColor(d.name, i),
                        marginRight: 3,
                        verticalAlign: 'middle',
                      }}
                    />
                    {d.name} ({d.value})
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Painel de salas — um card por turno, grid sem scroll horizontal */}
          {turmasPorTurno.length > 0 ? (
            <div className="ac-war-salas-por-turno ac-war-row-full">
              <div className="ac-war-mapa-legend ac-war-salas-legend">
                {PONTO_LEGENDA.map((l) => (
                  <span key={l.label} className="ac-war-leg-item" title={l.desc}>
                    <span className="ac-war-leg-dot" style={{ background: l.cor }} />
                    {l.label}
                  </span>
                ))}
              </div>
              {turmasPorTurno.map(({ turno, turmas }) => {
                const sum = turmas.reduce(
                  (acc, t) => {
                    const futuro = t.fase === 'futuro' || !t.contabiliza;
                    const total = t.matriculados || 0;
                    const p = futuro ? 0 : t.presentes || 0;
                    const at = futuro ? 0 : t.atrasados || 0;
                    const au = futuro ? 0 : t.ausentes || 0;
                    const entrou = futuro ? 0 : t.entraramRfid ?? p + at;
                    const ag = futuro ? total : Math.max(0, total - entrou);
                    acc.real += entrou;
                    acc.aus += au;
                    acc.aguard += ag;
                    return acc;
                  },
                  { real: 0, aus: 0, aguard: 0 },
                );
                return (
                  <div key={turno} className="ac-war-card ac-war-mapa">
                    <div className="ac-war-card-head">
                      <h3>
                        <MapIcon size={12} /> Salas · {turno} · RFID
                      </h3>
                      <span style={{ fontSize: '0.58rem', color: '#64748b' }}>
                        {turmas.length} turmas · {sum.real} real · {sum.aus} aus. · {sum.aguard} aguard.
                      </span>
                    </div>
                    <div className="ac-war-card-body">
                      <div className="ac-war-salas-grid">
                        {turmas.map((t: any) => {
                          const total = t.matriculados || 1;
                          const cols = colunasSala(total);
                          const futuro = t.fase === 'futuro' || !t.contabiliza;
                          const presentesCount = futuro ? 0 : t.presentes || 0;
                          const atrasadosCount = futuro ? 0 : t.atrasados || 0;
                          const ausentesCount = futuro ? 0 : t.ausentes || 0;
                          const aguardandoCount = futuro
                            ? total
                            : Math.max(0, total - presentesCount - atrasadosCount - ausentesCount);

                          const pontos: string[] = [];
                          for (let i = 0; i < presentesCount; i++) pontos.push('presente');
                          for (let i = 0; i < atrasadosCount; i++) pontos.push('atrasado');
                          for (let i = 0; i < ausentesCount; i++) pontos.push('ausente');
                          for (let i = 0; i < aguardandoCount; i++) pontos.push('aguardando');

                          const fileiras = pontosEmFileiras(pontos, cols);
                          const turmaId = String(t.turma?.id || `${turno}-${t.turma?.nome}`);
                          const nomeTurma = String(t.turma?.nome || 'Turma');
                          const entraramRfid = futuro
                            ? 0
                            : (t.entraramRfid ?? presentesCount + atrasadosCount);

                          return (
                            <button
                              type="button"
                              key={turmaId}
                              className={`ac-war-sala-card${futuro ? ' is-futuro' : ''}`}
                              onClick={() =>
                                setTurmaDetalhe({
                                  turmaId: String(t.turma?.id || ''),
                                  nome: nomeTurma,
                                  turno,
                                  futuro,
                                  matriculados: total,
                                  presentes: presentesCount,
                                  atrasados: atrasadosCount,
                                  ausentes: ausentesCount,
                                  aguardando: aguardandoCount,
                                  entraramRfid,
                                })
                              }
                            >
                              <header className="ac-war-sala-head">
                                <div className="ac-war-sala-title" title={nomeTurma}>
                                  {nomeTurma}
                                </div>
                                <div className="ac-war-sala-meta">
                                  {turno}
                                  {futuro ? ' · fut.' : ''}
                                </div>
                                <div className="ac-war-sala-stats">
                                  {futuro ? (
                                    <span className="c-aguard" title="Aguardando">
                                      {aguardandoCount}
                                    </span>
                                  ) : (
                                    <>
                                      <span className="c-presente" title="Presentes">
                                        {presentesCount}
                                      </span>
                                      <span className="c-atrasado" title="Atrasados">
                                        {atrasadosCount}
                                      </span>
                                      <span className="c-ausente" title="Ausentes">
                                        {ausentesCount}
                                      </span>
                                      <span className="c-aguard" title="Sem registro">
                                        {aguardandoCount}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </header>
                              <div className="ac-war-sala-room">
                                <div className="ac-war-sala-quadro" aria-hidden>
                                  <span>Quadro</span>
                                </div>
                                <div className="ac-war-sala-fileiras">
                                  {fileiras.map((fileira, fi) => (
                                    <div
                                      key={fi}
                                      className="ac-war-sala-fileira"
                                      style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
                                    >
                                      {fileira.map((p, pi) => (
                                        <span key={pi} className={pontoClass(p)} />
                                      ))}
                                    </div>
                                  ))}
                                </div>
                                <div className="ac-war-sala-piso" aria-hidden />
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="ac-war-card ac-war-row-full">
              <div className="ac-war-card-body" style={{ padding: 16, textAlign: 'center', color: '#64748b' }}>
                Nenhuma turma no turno {turnoAtual}.
              </div>
            </div>
          )}

          {/* Alertas */}
          <div className="ac-war-card">
            <div className="ac-war-card-head">
              <h3>
                <Zap size={12} /> Alertas críticos
              </h3>
              <span style={{ fontSize: '0.62rem', fontWeight: 800, color: '#ef4444' }}>{alertasCount}</span>
            </div>
            <div className="ac-war-card-body" style={{ maxHeight: 280, overflowY: 'auto' }}>
              {alertas.map((a: any) => {
                const turnoAlerta = canonicalTurnoLabel(
                  a.turno || a.turma?.turnoOperacional?.nome || a.turma?.turno || '',
                );
                const turmaNome = a.turma?.nome || '';
                const meta = [
                  turmaNome || null,
                  turnoAlerta !== 'Sem turno' ? turnoAlerta : null,
                  a.dataHoraReferencia || a.criadoEm
                    ? new Date(a.dataHoraReferencia || a.criadoEm).toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <div key={a.id} className={`ac-war-alerta ac-war-alerta--${a.severidade?.toLowerCase() || 'atencao'}`}>
                    <AlertTriangle
                      size={14}
                      style={{
                        flexShrink: 0,
                        marginTop: 1,
                        color: a.severidade === 'CRITICA' ? '#ef4444' : a.severidade === 'ALTA' ? '#d97706' : '#3b82f6',
                      }}
                    />
                    <div className="ac-war-alerta-body">
                      <strong>{a.titulo || a.descricao || 'Alerta do sistema'}</strong>
                      <span>{meta || 'Portaria'}</span>
                    </div>
                  </div>
                );
              })}
              {alertas.length === 0 && (
                <div style={{ padding: 12, textAlign: 'center', color: '#64748b' }}>
                  Nenhum alerta crítico no momento
                </div>
              )}
            </div>
          </div>

          {/* Escolas + RFID (Para Redes) */}
          {!isEscola && statusEstados.length > 0 && (
            <div className="ac-war-card">
              <div className="ac-war-card-head">
                <h3>
                  <Building2 size={12} /> Status por Estado
                </h3>
              </div>
              <div className="ac-war-card-body">
                {statusEstados.map((e: any) => {
                  const pct = e.taxaPresenca || 0;
                  return (
                    <div key={e.estado.id} className="ac-war-escola-bar">
                      <span className="ac-war-escola-name" title={e.estado.nome}>
                        {e.estado.sigla || e.estado.nome}
                      </span>
                      <div className="ac-war-escola-track">
                        <div className="ac-war-escola-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="ac-war-escola-pct">{pct}%</span>
                    </div>
                  );
                })}
                <div className="ac-war-rfid-panel" style={{ marginTop: 10 }}>
                  <Cpu size={20} style={{ color: '#16a34a', marginBottom: 4 }} />
                  <div className="ac-war-rfid-val">
                    {leitoresOnline}/{leitoresTotal}
                  </div>
                  <div className="ac-war-rfid-sub">Leitores RFID online na rede</div>
                </div>
              </div>
            </div>
          )}

          {/* Top atrasos turmas + alunos lado a lado */}
          <div className="ac-war-card">
            <div className="ac-war-card-head">
              <h3>
                <Clock size={12} /> Top atrasos (turmas)
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.58rem', color: '#64748b' }}>
                  {atrasosTurmasAll.length > 0
                    ? `${atrasosTurmasAll.reduce((s, t) => s + t.valor, 0)} alunos`
                    : 'sem atrasos'}
                </span>
                <button
                  type="button"
                  className="ac-war-report-btn"
                  onClick={() => setRelatorioAtrasos('turmas')}
                  title="Relatório completo de atrasos por turma"
                >
                  <FileText size={11} /> Relatório
                </button>
              </div>
            </div>
            <div className="ac-war-card-body">
              {topAtrasos.length > 0 ? (
                <div className="ac-war-chart-h-sm" style={{ height: Math.max(120, topAtrasos.length * 28) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={topAtrasos}
                      layout="vertical"
                      margin={{ top: 0, right: 12, left: 4, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis
                        type="number"
                        allowDecimals={false}
                        tick={{ fontSize: 9, fill: '#94a3b8' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="turma"
                        tick={{ fontSize: 9, fill: '#64748b' }}
                        width={118}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={(v) => [`${Number(v ?? 0)} aluno(s)`, 'Atrasos']}
                        labelFormatter={(label) => String(label)}
                      />
                      <Bar dataKey="valor" fill="#ef4444" radius={[0, 4, 4, 0]} name="Atrasos" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div style={{ padding: 12, textAlign: 'center', color: '#64748b', fontSize: '0.68rem' }}>
                  Nenhum atraso registrado no escopo atual
                  {vistaEscola ? ' (escola)' : ` (${turnoAtual})`}.
                </div>
              )}
            </div>
          </div>

          <div className="ac-war-card">
            <div className="ac-war-card-head">
              <h3>
                <Users size={12} /> Top atrasos (alunos)
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.58rem', color: '#64748b' }}>
                  {atrasosAlunosAll.length > 0 ? `${atrasosAlunosAll.length} alunos` : 'sem atrasos'}
                </span>
                <button
                  type="button"
                  className="ac-war-report-btn"
                  onClick={() => setRelatorioAtrasos('alunos')}
                  title="Relatório completo de atrasos por aluno"
                >
                  <FileText size={11} /> Relatório
                </button>
              </div>
            </div>
            <div className="ac-war-card-body">
              {topAtrasosAlunos.length > 0 ? (
                <div className="ac-war-chart-h-sm" style={{ height: Math.max(120, topAtrasosAlunos.length * 28) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={topAtrasosAlunos}
                      layout="vertical"
                      margin={{ top: 0, right: 12, left: 4, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis
                        type="number"
                        allowDecimals={false}
                        tick={{ fontSize: 9, fill: '#94a3b8' }}
                        axisLine={false}
                        tickLine={false}
                        unit=" min"
                      />
                      <YAxis
                        type="category"
                        dataKey="chartLabel"
                        tick={{ fontSize: 9, fill: '#64748b' }}
                        width={118}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={(v) => [`${Number(v ?? 0)} min`, 'Atraso']}
                        labelFormatter={(_, payload) => {
                          const row = payload?.[0]?.payload;
                          if (!row) return '';
                          return `${row.nome} · ${row.turma} · ${canonicalTurnoLabel(row.turno || '')} · ${row.hora}`;
                        }}
                      />
                      <Bar dataKey="valor" fill="#f59e0b" radius={[0, 4, 4, 0]} name="Min. atraso" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div style={{ padding: 12, textAlign: 'center', color: '#64748b', fontSize: '0.68rem' }}>
                  Nenhum aluno atrasado no escopo atual
                  {vistaEscola ? ' (escola)' : ` (${turnoAtual})`}.
                </div>
              )}
            </div>
          </div>

          {/* Feed ao vivo */}
          <div className="ac-war-card ac-war-row-full">
            <div className="ac-war-card-head">
              <h3>
                <DoorOpen size={12} /> Pulso da portaria — últimos eventos
              </h3>
              <span style={{ fontSize: '0.58rem', color: '#22c55e', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', animation: 'ac-war-pulse 2s infinite' }} />
                Streaming
              </span>
            </div>
            <div className="ac-war-card-body">
              <div className="ac-war-feed">
                <div className="ac-war-feed-row ac-war-feed-h">
                  <span>Hora</span>
                  <span>Aluno</span>
                  <span>Turma</span>
                  <span>Tipo</span>
                  <span>Local</span>
                </div>
                {feed.map((ev: any, idx: number) => {
                  const tipoRaw = String(ev.tipo || ev.tipoEvento || '').toUpperCase();
                  const isEntrada = tipoRaw.includes('ENTRADA');
                  const tipoLabel = isEntrada ? 'Entrada' : 'Saída';
                  const nome = ev.nome || ev.aluno?.nome || 'Desconhecido';
                  const turma = ev.turma || ev.aluno?.turma?.nome || '—';
                  const local = ev.porta || ev.dispositivo?.localInstalacao || 'Portaria';
                  const hora =
                    ev.hora ||
                    (ev.dataHora
                      ? new Date(ev.dataHora).toLocaleTimeString('pt-BR', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '—');
                  return (
                    <div key={ev.id || `feed-${idx}`} className="ac-war-feed-row">
                      <span className="ac-war-feed-hora">{hora}</span>
                      <span className="ac-war-feed-cell ac-war-feed-aluno" title={nome}>
                        {nome}
                      </span>
                      <span className="ac-war-feed-cell ac-war-feed-turma" title={turma}>
                        {turma}
                      </span>
                      <span
                        className="ac-war-feed-pill"
                        title={tipoLabel}
                        style={{
                          background: isEntrada ? '#dcfce7' : '#fee2e2',
                          color: isEntrada ? '#166534' : '#991b1b',
                        }}
                      >
                        {isEntrada ? <LogIn size={10} /> : <LogOut size={10} />}
                        {tipoLabel}
                      </span>
                      <span
                        className="ac-war-feed-pill ac-war-feed-pill--local"
                        title={local}
                        style={{ background: '#f1f5f9', color: '#475569' }}
                      >
                        {local}
                      </span>
                    </div>
                  );
                })}
                {feed.length === 0 && (
                  <div style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>
                    Nenhum evento registrado hoje.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {turmaDetalhe && (
        <div
          role="dialog"
          aria-modal="true"
          className="ac-war-report-overlay"
          onClick={() => setTurmaDetalhe(null)}
        >
          <div className="ac-war-report-modal" onClick={(e) => e.stopPropagation()}>
            <header className="ac-war-report-head">
              <div>
                <h2>
                  {turmaDetalhe.nome} · {turmaDetalhe.turno}
                </h2>
                <p>
                  {dataRefLabel}
                  {turmaDetalhe.futuro ? ' · turno futuro (ainda não contabiliza)' : ' · detalhe RFID'}
                </p>
              </div>
              <button
                type="button"
                className="ac-war-report-close"
                onClick={() => setTurmaDetalhe(null)}
                aria-label="Fechar"
              >
                <X size={16} />
              </button>
            </header>
            <div className="ac-war-report-body">
              <div className="ac-war-turma-kpis">
                <div>
                  <strong>{turmaDetalhe.matriculados}</strong>
                  <span>Matriculados</span>
                </div>
                <div>
                  <strong>{turmaDetalhe.entraramRfid}</strong>
                  <span>Entraram</span>
                </div>
                <div className="ok">
                  <strong>{turmaDetalhe.presentes}</strong>
                  <span>Presentes</span>
                </div>
                <div className="warn">
                  <strong>{turmaDetalhe.atrasados}</strong>
                  <span>Atrasados</span>
                </div>
                <div className="bad">
                  <strong>{turmaDetalhe.ausentes}</strong>
                  <span>Ausentes</span>
                </div>
                <div>
                  <strong>{turmaDetalhe.aguardando}</strong>
                  <span>Sem registro</span>
                </div>
              </div>
              <h3 className="ac-war-turma-sub">
                Ausentes ({ausentesAlunosTurmaDetalhe.length})
              </h3>
              {turmaDetalhe.futuro ? (
                <div className="ac-war-report-empty">Turno ainda não iniciado.</div>
              ) : ausentesAlunosTurmaDetalhe.length === 0 ? (
                <div className="ac-war-report-empty">Nenhum ausente nesta turma.</div>
              ) : (
                <table className="ac-war-report-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Aluno</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ausentesAlunosTurmaDetalhe.map((a, i) => (
                      <tr key={a.alunoId || `${a.nome}-${i}`}>
                        <td>{i + 1}</td>
                        <td>{a.nome}</td>
                        <td className="num accent">Sem entrada</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <h3 className="ac-war-turma-sub" style={{ marginTop: 16 }}>
                Atrasados ({atrasosAlunosTurmaDetalhe.length})
              </h3>
              {turmaDetalhe.futuro ? (
                <div className="ac-war-report-empty">Turno ainda não iniciado.</div>
              ) : atrasosAlunosTurmaDetalhe.length === 0 ? (
                <div className="ac-war-report-empty">
                  Nenhum atraso registrado nesta turma.
                </div>
              ) : (
                <table className="ac-war-report-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Aluno</th>
                      <th>Entrada</th>
                      <th>Atraso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {atrasosAlunosTurmaDetalhe.map((a, i) => (
                      <tr key={a.alunoId || `${a.nome}-${i}`}>
                        <td>{i + 1}</td>
                        <td>{a.nome}</td>
                        <td className="num">{a.hora}</td>
                        <td className="num accent">{a.minutosAtraso} min</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <footer className="ac-war-report-foot">
              <button type="button" onClick={() => setTurmaDetalhe(null)}>
                Fechar
              </button>
            </footer>
          </div>
        </div>
      )}

      {relatorioAtrasos && (
        <div
          role="dialog"
          aria-modal="true"
          className="ac-war-report-overlay"
          onClick={() => setRelatorioAtrasos(null)}
        >
          <div className="ac-war-report-modal" onClick={(e) => e.stopPropagation()}>
            <header className="ac-war-report-head">
              <div>
                <h2>
                  {relatorioAtrasos === 'turmas'
                    ? 'Relatório · Atrasos por turma'
                    : 'Relatório · Atrasos por aluno'}
                </h2>
                <p>
                  {dataRefLabel} · {vistaEscola ? 'Escola consolidada' : `Turno ${turnoAtual}`}
                  {' · '}
                  {relatorioAtrasos === 'turmas'
                    ? `${atrasosTurmasAll.length} turmas`
                    : `${atrasosAlunosAll.length} alunos`}
                </p>
              </div>
              <button
                type="button"
                className="ac-war-report-close"
                onClick={() => setRelatorioAtrasos(null)}
                aria-label="Fechar"
              >
                <X size={16} />
              </button>
            </header>
            <div className="ac-war-report-body">
              {relatorioAtrasos === 'turmas' ? (
                atrasosTurmasAll.length === 0 ? (
                  <div className="ac-war-report-empty">Nenhuma turma com atraso.</div>
                ) : (
                  <table className="ac-war-report-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Turma</th>
                        <th>Turno</th>
                        <th>Atrasados</th>
                        <th>Matriculados</th>
                      </tr>
                    </thead>
                    <tbody>
                      {atrasosTurmasAll.map((t, i) => (
                        <tr key={t.id}>
                          <td>{i + 1}</td>
                          <td>{t.label.replace(` · ${t.turno}`, '')}</td>
                          <td>{t.turno}</td>
                          <td className="num accent">{t.valor}</td>
                          <td className="num">{t.matriculados || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              ) : atrasosAlunosAll.length === 0 ? (
                <div className="ac-war-report-empty">Nenhum aluno atrasado.</div>
              ) : (
                <table className="ac-war-report-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Aluno</th>
                      <th>Turma</th>
                      <th>Turno</th>
                      <th>Entrada</th>
                      <th>Atraso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {atrasosAlunosAll.map((a, i) => (
                      <tr key={a.alunoId || `${a.nome}-${i}`}>
                        <td>{i + 1}</td>
                        <td>{a.nome}</td>
                        <td>{a.turma}</td>
                        <td>{canonicalTurnoLabel(a.turno || '')}</td>
                        <td className="num">{a.hora}</td>
                        <td className="num accent">{a.minutosAtraso} min</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <footer className="ac-war-report-foot">
              <button type="button" onClick={() => setRelatorioAtrasos(null)}>
                Fechar
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
